import {EmailTemplatePreview, Workplace, WorkplaceLink} from "models"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component"
import Button from "components/inputs/button"
import ContentsErrors from "components/contents/errors"
import {digg} from "diggerize"
import {EmailTemplatePreviewsSelectWithContainer} from "components/email-template-previews/select"
import {FlashNotifications as FlashMessage} from "flash-notifications"
import {loading} from "components/layout/loading-blocker"
import LoadingSpinner from "components/layout/loading-spinner"
import memo from "set-state-compare/build/memo"
import Modal from "components/modal"
import PropTypes from "prop-types"
import React from "react"
import {Text} from "shared/base"
import useI18n from "i18n-on-steroids/build/src/use-i18n.js"
import {View} from "react-native"

/**
 * @typedef {object} ComponentsUsersSendEmailPreviewButtonProps
 * @property {boolean} disabled
 */

/**
 * @typedef {object} ComponentsUsersSendEmailPreviewButtonState
 * @property {boolean} disabled
 * @property {unknown} liquidErrors
 * @property {unknown} liquidErrorUser
 * @property {boolean} showSendEmailPreview
 * @property {unknown} usersCount
 */

/**
 * @extends {ShapeComponent<ComponentsUsersSendEmailPreviewButtonProps, ComponentsUsersSendEmailPreviewButtonState>}
 */
class ComponentsUsersSendEmailPreviewButton extends ShapeComponent {
  /** @type {ComponentsUsersSendEmailPreviewButtonState} */
  state = {
    disabled: false,
    liquidErrorUser: undefined,
    liquidErrors: undefined,
    showSendEmailPreview: false,
    usersCount: undefined
  }

  static propTypes = {
    disabled: PropTypes.bool
  }

  setup() {
    const {t} = useI18n({namespace: "js.components.users.send_email_preview_button"})

    this.t = t
  }

  render() {
    const {t} = this.tt
    const {liquidErrorUser, liquidErrors, showSendEmailPreview, usersCount} = this.s

    return (
      <>
        {showSendEmailPreview &&
          <Modal onRequestClose={this.tt.onRequestCloseModal}>
            {usersCount == undefined &&
              <LoadingSpinner />
            }
            {usersCount !== undefined &&
              <>
                <View testID="send-email-preview-modal-content">
                  <View style={styles.sendToCountUsers ||= {marginBottom: 16}} testID="send-email-preview-user-count">
                    <Text>
                      {t(".send_to_count_users", {count: usersCount})}
                    </Text>
                  </View>
                  {liquidErrors?.length > 0 &&
                    <View style={styles.liquidErrors ||= {marginBottom: 16}} testID="send-email-preview-liquid-errors">
                      <Text
                        style={styles.liquidErrorsTitle ||= {color: "red", fontWeight: 700, marginBottom: 8}}
                        testID="send-email-preview-liquid-errors-title"
                      >
                        {t(".preview_could_not_be_generated")}
                      </Text>
                      {liquidErrorUser &&
                        <Text
                          style={styles.liquidErrorsHelp ||= {marginBottom: 8}}
                          testID="send-email-preview-liquid-errors-help"
                        >
                          {t(".fix_the_preview_for_user", {locale: liquidErrorUser.locale, userName: liquidErrorUser.name})}
                        </Text>
                      }
                      <ContentsErrors errors={liquidErrors} />
                    </View>
                  }
                </View>
                <form onSubmit={this.tt.onSubmit}>
                  <EmailTemplatePreviewsSelectWithContainer
                    id="email_template_preview_id"
                    label={EmailTemplatePreview.modelName().human()}
                    name="email_template_preview_id"
                    onChange={this.tt.onPreviewChanged}
                  />
                  <Button label={t(".send_emails")} primary />
                </form>
              </>
            }
          </Modal>
        }
        <Button
          className="components--users--send-email-preview-button"
          disabled={this.s.disabled || this.props.disabled}
          icon="envelope"
          label={t(".send_email_preview")}
          onClick={this.tt.onSendEmailPreviewClicked}
        />
      </>
    )
  }

  onPreviewChanged = () => this.setState({liquidErrorUser: undefined, liquidErrors: undefined})

  onRequestCloseModal = () => this.setState({liquidErrorUser: undefined, liquidErrors: undefined, showSendEmailPreview: false, usersCount: undefined})

  countUsersSelected = async () => {
    const currentWorkplaceResult = await Workplace.current()
    const currentWorkplace = digg(currentWorkplaceResult, "current", 0)
    const usersCount = await WorkplaceLink
      .ransack({
        resource_type_eq: "User",
        workplace_id_eq: currentWorkplace.id()
      })
      .count()

    this.setState({usersCount})
  }

  onSendEmailPreviewClicked = () => {
    this.countUsersSelected()
    this.setState({liquidErrorUser: undefined, liquidErrors: undefined, showSendEmailPreview: true})
  }

  onSubmit = async (e) => {
    e.preventDefault()

    const form = digg(e, "target")
    const formData = new FormData(form)
    const emailTemplatePreviewId = formData.get("email_template_preview_id")
    const emailTemplatePreview = await EmailTemplatePreview.find(emailTemplatePreviewId)

    try {
      this.setState({disabled: true})
      await loading(this.t("js.shared.loading"), async () => {
        const result = await emailTemplatePreview.sendEmailsToWorkplaceUsers()

        if (result?.liquid_errors?.length > 0) {
          this.setState({liquidErrorUser: result.liquid_error_user, liquidErrors: result.liquid_errors})

          return
        }

        this.setState({liquidErrorUser: undefined, liquidErrors: undefined, showSendEmailPreview: false})
      })
      if (!this.s.liquidErrors || this.s.liquidErrors.length == 0) {
        FlashMessage.success(this.t(".the_email_previews_has_been_sent"))
      }
    } catch (error) {
      FlashMessage.errorResponse(error)
    } finally {
      this.setState({disabled: false})
    }
  }
}

export default memo(shapeComponent(ComponentsUsersSendEmailPreviewButton))

/** @type {Record<string, object>} */
const styles = {}
